import React, { useState } from 'react';
import { Heart, Copy, CheckCircle, CreditCard, Smartphone, Building2 } from 'lucide-react';
import { donationInfo } from '../data/mock';

const DonationSection = () => {
  const [copiedUPI, setCopiedUPI] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(false);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'upi') {
      setCopiedUPI(true);
      setTimeout(() => setCopiedUPI(false), 2000);
    } else {
      setCopiedAccount(true);
      setTimeout(() => setCopiedAccount(false), 2000);
    }
  };

  const handleUPIPayment = () => {
    // This will open UPI apps on mobile devices
    const upiUrl = `upi://pay?pa=${donationInfo.upiId}&pn=Mass Movement For Transformation`;
    window.location.href = upiUrl;
  };

  return (
    <section id="donate" className="py-20 bg-gradient-to-br from-green-50 via-orange-50 to-yellow-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-4">
            <Heart className="w-5 h-5 text-green-600 fill-current" />
            <span className="text-sm font-semibold text-green-700">Support Our Mission</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Make a Difference Today
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Your generous contribution helps us spread Buddhist teachings, organize social programs, and empower communities towards Prabuddha Bharath.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* UPI Payment Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 transition-transform duration-300">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Smartphone className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold">UPI Payment</h3>
              </div>
              <p className="text-green-100">Quick & Easy - Scan or Pay</p>
            </div>

            <div className="p-8">
              {/* UPI QR Code */}
              <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-xl mb-6 border-2 border-green-200">
                <img 
                  src={donationInfo.qrCode} 
                  alt="UPI QR Code" 
                  className="w-full max-w-xs mx-auto rounded-lg shadow-lg"
                />
              </div>

              {/* UPI ID */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">UPI ID</label>
                <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="flex-1 font-mono text-lg text-gray-900">{donationInfo.upiId}</span>
                  <button
                    onClick={() => copyToClipboard(donationInfo.upiId, 'upi')}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    {copiedUPI ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Pay Now Button */}
              <button
                onClick={handleUPIPayment}
                className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <CreditCard className="w-5 h-5" />
                Pay with UPI
              </button>
              <p className="text-xs text-gray-500 text-center mt-3">
                Works with Google Pay, PhonePe, Paytm & more
              </p>
            </div>
          </div>

          {/* Bank Transfer Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 transition-transform duration-300">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold">Bank Transfer</h3>
              </div>
              <p className="text-orange-100">Direct Bank Account Details</p>
            </div>

            <div className="p-8">
              {/* Bank Details */}
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-l-4 border-orange-600">
                  <p className="text-sm text-gray-600 mb-1">Bank Name</p>
                  <p className="text-lg font-semibold text-gray-900">{donationInfo.bankDetails.bankName}</p>
                </div>

                <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-l-4 border-orange-600">
                  <p className="text-sm text-gray-600 mb-1">Account Holder Name</p>
                  <p className="text-lg font-semibold text-gray-900">{donationInfo.bankDetails.accountName}</p>
                </div>

                <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-l-4 border-orange-600">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-1">Account Number</p>
                      <p className="text-lg font-semibold text-gray-900 font-mono">{donationInfo.bankDetails.accountNumber}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(donationInfo.bankDetails.accountNumber, 'account')}
                      className="p-2 hover:bg-orange-200 rounded-lg transition-colors"
                    >
                      {copiedAccount ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Copy className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-l-4 border-orange-600">
                    <p className="text-sm text-gray-600 mb-1">IFSC Code</p>
                    <p className="text-lg font-semibold text-gray-900 font-mono">{donationInfo.bankDetails.ifscCode}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-l-4 border-orange-600">
                    <p className="text-sm text-gray-600 mb-1">Branch</p>
                    <p className="text-sm font-semibold text-gray-900">{donationInfo.bankDetails.branch}</p>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Please send us a confirmation email at president@mmtorg.in after making the donation with your transaction details.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Impact Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Your Impact</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Education</h4>
              <p className="text-gray-600">Support Dhamma education programs and Buddhist conferences for children and women</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Community</h4>
              <p className="text-gray-600">Help create self-help groups and provide entrepreneur support to Buddhist community</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-orange-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Social Change</h4>
              <p className="text-gray-600">Spread awareness about Buddhist philosophy and Dr. Ambedkar's vision for social equality</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationSection;